import { NavLink } from 'react-router-dom'
import './AppShell.css'

/** Where a tab lives, and what it is called. */
const TABS = [
  { to: '/inventory/apps', label: 'Apps' },
  { to: '/inventory/endpoints', label: 'Endpoints' },
  { to: '/inventory/integrations', label: 'Integrations' },
  { to: '/inventory/identities', label: 'Identities' },
] as const

/**
 * The console's chrome, around whatever the route renders.
 *
 * It carries no data, which is what lets it be built and styled before
 * HOR-223 delivers anything to put in it.
 *
 * `title` names the page and ends the breadcrumb, so a wrong URL does not sit
 * under a heading claiming to be the inventory. `tabs` is off by default
 * because only Inventory has any. `counts` puts a number beside a tab, and
 * only for a tab whose number was actually read. `footer` sits at the foot of the rail; the
 * shell takes it as a slot rather than reading the session itself, so it can
 * be rendered and tested without one.
 */
export function AppShell({
  title,
  tabs = false,
  counts = {},
  footer,
  children,
}: {
  title: string
  tabs?: boolean
  counts?: Readonly<Record<string, number | undefined>>
  footer?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="shell">
      <nav className="rail" aria-label="Console sections">
        {/* The wordmark the design draws, and the name the product has. A
            screen reader gets no logo, so it gets the full name instead; the
            two are separate nodes because an accessible name is computed by
            joining text, not by reading whitespace between elements. */}
        <h1 className="wordmark">
          <span aria-hidden="true">Horai</span>
          <span className="sr-only">Horai Console</span>
        </h1>
        <ul className="rail-sections">
          {/* Reference context from the design, deliberately inert: these are
              not deliverables, and a link to a screen that does not exist is
              worse than no link. Removing them makes the navigation look
              broken instead. */}
          {['Dashboard', 'Risks', 'Policies'].map((label) => (
            <li key={label} className="rail-section is-inert">
              {label}
            </li>
          ))}
          <li className="rail-section">
            <NavLink
              to="/inventory"
              className={({ isActive }) => (isActive ? 'rail-link active' : 'rail-link')}
            >
              Inventory
            </NavLink>
          </li>
        </ul>
        {footer !== undefined && <div className="rail-footer">{footer}</div>}
      </nav>

      <div className="canvas">
        <header className="page-header">
          <nav aria-label="Breadcrumb" className="crumbs">
            <ol>
              <li>Horai</li>
              <li aria-current="page">{title}</li>
            </ol>
          </nav>
          <h2 className="page-title">{title}</h2>
          {tabs && (
            <nav aria-label="Inventory views" className="tabs">
              {TABS.map(({ to, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) => (isActive ? 'tab active' : 'tab')}
                >
                  {label}
                  {counts[label] === undefined ? null : (
                    <span className="tab-count">{counts[label]}</span>
                  )}
                </NavLink>
              ))}
            </nav>
          )}
        </header>

        <main className="page">{children}</main>
      </div>
    </div>
  )
}
